import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Button,
  GridTable,
  SecondaryHeader,
  SecondaryHeaderButtonWrap,
  SecondaryHeaderInfo,
  SecondaryHeaderSearchField,
  Spinner,
} from "olive-react-myoneui";
import { ToastContainer, toast } from "react-toastify";
import AddEditOffcanvas from "./AddEditOffcanvas";
import { getChapters } from "../../../api/chapterApi";
import {
  createSection,
  deleteSection,
  getSections,
  updateSection,
} from "../../../api/sectionApi";
import DataNotFound from "../component/DataNotFound";
import PaginationTemplate from "../component/grid-table/PaginationTemplate";
import TextTemplate from "../component/grid-table/TextTemplate";
import { penEditIcon, removeIcon } from "../../../constants/icons";
import { formatDate, debounce } from "../../../utils/helper";
import ConfirmBox from "../component/ConfirmBox";

const Section = () => {
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [title, setTitle] = useState("");
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [chapterPage, setChapterPage] = useState(1);
  const [searchChapter, setSearchChapter] = useState("");
  const [chapterLoading, setChapterLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sectionCreateUpdateLoading, setSectionCreateUpdateLoading] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [sectionLimit, setSectionLimit] = useState(20);
  const [totalSections, setTotalSections] = useState(0);
  const [totalSectionPage, setTotalSectionPage] = useState(0);
  const [currentSectionPage, setCurrentSectionPage] = useState(1);
  const [type, setType] = useState("add");
  const [errors, setErrors] = useState({});
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [showConfirmBox, setShowConfirmBox] = useState(false);
  const [deleteSectionLoading, setDeleteSectionLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const updateSearchWithDebounce = useCallback(
    debounce((val) => {
      setDebouncedSearch(val);
      setCurrentSectionPage(1);
    }, 500),
    [],
  );

  const onSearchChange = (e) => {
    setSearch(e.target.value);
    updateSearchWithDebounce(e.target.value);
  };

  const onClearSearch = () => {
    setSearch("");
    updateSearchWithDebounce("");
  };

  const tableManager = useRef(null);
  const setTableManager = (tm) => (tableManager.current = tm);

  const handleClose = () => {
    handleClear();
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    if (name === "title") {
      setTitle(value);
      if (errors.title) setErrors((prev) => ({ ...prev, title: null }));
    }
  };

  const fetchChapters = async (page = 1, searchParam = searchChapter) => {
    setChapterLoading(true);
    if (page === 1) setChapters([]);
    try {
      const response = await getChapters({
        page,
        limit: 20,
        search:
          searchParam && searchParam?.trim()
            ? encodeURIComponent(searchParam.trim())
            : "",
      });

      if (response?.success) {
        const newData = response?.data || [];
        const metadata = response?.metadata || {};

        const totalPages = metadata?.totalPages || 1;
        const currentPage = metadata?.page || page;

        if (currentPage === 1) {
          setChapters(newData);
          setChapterPage(currentPage);
        } else {
          setChapters((prev) => [...prev, ...newData]);
          setChapterPage(currentPage);
        }

        setHasMore(currentPage < totalPages);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setChapterLoading(false);
    }
  };

  const handleOffCanvas = () => {
    setShowOffcanvas(true);
    fetchChapters(1);
  };

  const handleLoadMore = () => {
    if (chapterLoading || !hasMore) return;
    setChapterPage((prev) => {
      const nextPage = prev + 1;
      fetchChapters(nextPage);
      return nextPage;
    });
  };

  const handleClear = () => {
    setTitle("");
    setSelectedChapter(null);
    setChapters([]);
    setChapterPage(1);
    setShowOffcanvas(false);
    setType("add");
    setSelectedSectionId("");
    setShowConfirmBox(false);
    setErrors({});
  };

  const handleSubmit = () => {
    let newErrors = {};
    if (!title?.trim()) {
      newErrors.title = "Section title is required";
    }
    if (!selectedChapter) {
      newErrors.chapter = "Chapter is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const payload = {
      title,
      chapter: selectedChapter?._id,
    };

    setSectionCreateUpdateLoading(true);
    if (type === "add") {
      createSections(payload);
    } else if (type === "edit") {
      editSections(payload);
    }
  };

  const createSections = (payload) => {
    createSection(payload)
      .then((res) => {
        if (res?.success) {
          getPaginatedSections();
          toast.success("Section added successfully.");
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message);
        console.log(error);
      })
      .finally(() => {
        setSectionCreateUpdateLoading(false);
        handleClear();
      });
  };

  const editSections = (payload) => {
    updateSection(selectedSectionId, payload)
      .then((res) => {
        if (res?.success) {
          getPaginatedSections();
          toast.success("Section updated successfully.");
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message);
        console.log(error);
      })
      .finally(() => {
        setSectionCreateUpdateLoading(false);
        handleClear();
      });
  };

  const getPaginatedSections = (currentPage, limit, searchText) => {
    const payload = {
      page: currentPage || currentSectionPage,
      limit: limit || sectionLimit,
      search: searchText !== undefined ? searchText : debouncedSearch,
    };
    setLoading(true);
    getSections(payload)
      .then((response) => {
        if (response?.success) {
          setSections(response?.data || []);
          setSectionLimit(Number(response?.metadata?.perPage));
          setTotalSectionPage(Number(response?.metadata?.totalPages));
          setTotalSections(Number(response?.metadata?.totalItems));
          setCurrentSectionPage(Number(response?.metadata?.page));
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columnData = [
    {
      id: 0,
      visible: true,
      label: "S No.",
      width: "1fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: ({ rowIndex }) => <TextTemplate value={rowIndex} />,
    },
    {
      id: 1,
      field: "title",
      visible: true,
      label: "Section",
      width: "3fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: TextTemplate,
    },
    {
      id: 2,
      visible: true,
      label: "Chapter",
      width: "3fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: ({ data }) => (
        <TextTemplate value={data.chapter?.title || "N/A"} />
      ),
    },
    {
      id: 3,
      visible: true,
      label: "Created At",
      width: "2fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: ({ data }) => (
        <TextTemplate value={formatDate(data?.createdAt) || "N/A"} />
      ),
    },
    {
      id: 4,
      label: `Action`,
      width: "3fr",
      sortable: false,
      resizable: false,
      className: "grid-header-text-center",
      cellRenderer: ({ data }) => (
        <div className="rgt-cell-inner rgt-text-truncate program-edit-hover justify-content-center">
          <Button
            variant="white"
            className="btn-round"
            onClick={() => handleEdit(data)}
            disabled={loading}
          >
            {penEditIcon} Edit
          </Button>
          <Button
            variant="white"
            className="btn-round"
            onClick={() => handleDelete(data?._id)}
            disabled={loading}
          >
            {removeIcon} Delete
          </Button>
        </div>
      ),
    },
  ];

  const handleEdit = (data) => {
    if (!data?._id) return;
    setType("edit");
    setShowOffcanvas(true);
    setTitle(data?.title);
    setSelectedChapter(data?.chapter);
    setSelectedSectionId(data?._id);
    fetchChapters(1);
  };

  const deleteSelectedSection = () => {
    setDeleteSectionLoading(true);
    deleteSection(selectedSectionId)
      .then((res) => {
        if (res?.success) {
          handleClear();
          getPaginatedSections();
          toast.success("Section deleted successfully.");
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || "Error deleting section");
      })
      .finally(() => {
        setDeleteSectionLoading(false);
      });
  };

  const handleHideConfirmBox = () => {
    setShowConfirmBox(false);
    setSelectedSectionId("");
  };

  const handleDelete = (id) => {
    if (!id) return;
    setSelectedSectionId(id);
    setShowConfirmBox(id);
  };

  const handlePageSizeChange = (size) => {
    if (!size || Number(size) === Number(sectionLimit)) return;
    setSectionLimit(Number(size));
    setCurrentSectionPage(1);
  };

  const updateChapterSearchWithDebounce = useCallback(
    debounce((val) => {
      setChapterPage(1);
      fetchChapters(1, val);
    }, 500),
    [],
  );

  const handleSearchChapterChange = (val) => {
    setSearchChapter(val);
    updateChapterSearchWithDebounce(val);
  };

  useEffect(() => {
    getPaginatedSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionLimit, currentSectionPage, debouncedSearch]);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="content-wrapper">
        <SecondaryHeader>
          <SecondaryHeaderInfo
            SecondaryHeaderTitle="Sections"
            SecondaryHeaderText={
              totalSections === 0
                ? "No Sections"
                : `${totalSections} ${totalSections === 1 ? "Section" : "Sections"}`
            }
            SecondaryText={true}
          />
          <SecondaryHeaderSearchField
            placeholder="Search Sections..."
            value={search}
            onChange={onSearchChange}
            onClear={onClearSearch}
            disabled={loading || deleteSectionLoading}
          />
          <SecondaryHeaderButtonWrap>
            <Button
              variant="secondary"
              onClick={handleOffCanvas}
              disabled={loading}
            >
              Add
            </Button>
          </SecondaryHeaderButtonWrap>
        </SecondaryHeader>
        <div className="content-block p-2" style={{ position: "relative" }}>
          {loading ? (
            <div className="spinner-wrapper">
              <Spinner animation="border" />
            </div>
          ) : Array.isArray(sections) && sections.length > 0 ? (
            <GridTable
              key={sections.length}
              searchText=""
              highlightSearch={false}
              enableColumnsReorder={false}
              columns={columnData}
              onRowsRequest={() => ({
                rows: sections,
                totalRows: totalSections,
              })}
              page={currentSectionPage}
              pageSize={sectionLimit}
              setPage={setCurrentSectionPage}
              setPageSize={handlePageSizeChange}
              totalPages={totalSectionPage}
              onLoad={setTableManager}
              components={{
                Footer: (props) => (
                  <PaginationTemplate
                    {...props}
                    page={currentSectionPage}
                    pageSize={sectionLimit}
                    totalPages={totalSectionPage}
                    onPageChange={setCurrentSectionPage}
                    onPageSizeChange={handlePageSizeChange}
                    disabled={loading}
                  />
                ),
              }}
            />
          ) : (
            <DataNotFound title="No sections found" nochange={true} />
          )}
        </div>
      </div>

      <ConfirmBox
        show={showConfirmBox}
        question="Are you sure you want to delete Section?"
        okText="Delete"
        cancelText="Cancel"
        onHide={handleHideConfirmBox}
        onSubmit={deleteSelectedSection}
        buttonLoading={deleteSectionLoading}
      />

      <AddEditOffcanvas
        show={showOffcanvas}
        onClose={handleClose}
        type={type}
        handleChange={handleChange}
        title={title}
        selectedChapter={selectedChapter}
        chapters={chapters}
        setSelectedChapter={(val) => {
          setSelectedChapter(val);
          if (errors.chapter) setErrors((prev) => ({ ...prev, chapter: null }));
        }}
        searchValue={searchChapter}
        setSearchValue={handleSearchChapterChange}
        hasMore={hasMore}
        handleLoadMore={handleLoadMore}
        isLoading={chapterLoading}
        onSubmit={handleSubmit}
        onButtonLoading={sectionCreateUpdateLoading}
        errors={errors}
      />
    </>
  );
};

export default Section;
